import { useLayoutEffect, useRef, type ReactNode } from "react";
import * as THREE from "three";
import type { ThreeEvent } from "@react-three/fiber";

/**
 * One InstancedMesh for a static set of parts (ADR 199). The generalization of
 * OccupancyLayer's DeckInstances, extracted because JSL-scale profiling showed
 * drei's declarative `<Instances>/<Instance>` mounting a real scene-graph
 * object per instance: ~56k objects for the loads + rack frames, ~24% of every
 * frame in `updateMatrixWorld`, and a 68 s React mount. Here matrices (and
 * optional per-instance colors) are composed ONCE in a layout effect — scene
 * furniture never moves, so nothing runs per frame and the scene graph carries
 * one object regardless of count.
 *
 * Contract:
 * - Parents key the component on `items.length` — capacity is fixed at
 *   construction, so a membership change must remount (DeckInstances rule).
 * - `compose` writes item `i`'s local-frame matrix into `out` (the layer's
 *   group frame — rotated or not is the caller's concern).
 * - `geometry`/`material` accept shared objects (GLB parts); declarative
 *   children work too (`<boxGeometry>` + material tags).
 * - `frustumCulled` is off by default: instances spread across the warehouse
 *   while the shared geometry's bounds sit at the origin — culling by those
 *   bounds would drop the whole group as soon as the origin leaves view.
 *   `cullable` computes the REAL instance bounds instead and turns culling
 *   on — the right mode for spatially chunked sets (`chunkByGrid`).
 */
interface Props<T> {
  items: T[];
  compose: (item: T, out: THREE.Matrix4) => void;
  /** Per-instance tint, multiplied over the material (white = untouched). */
  colorOf?: (item: T) => string;
  geometry?: THREE.BufferGeometry;
  material?: THREE.Material | THREE.Material[];
  castShadow?: boolean;
  /** Compute instance-aware bounds and let the frustum cull this mesh. */
  cullable?: boolean;
  onClick?: (e: ThreeEvent<MouseEvent>) => void;
  onPointerOver?: (e: ThreeEvent<PointerEvent>) => void;
  onPointerMove?: (e: ThreeEvent<PointerEvent>) => void;
  onPointerOut?: (e: ThreeEvent<PointerEvent>) => void;
  /** ADR 371: which instances answer the pointer; undefined = all of them.
   *  Read at raycast time through a ref, so a new predicate never remounts
   *  or re-buckets the mesh (the ADR 212 rule for the pick volumes). A dead
   *  instance is simply not a hit: the ray carries on to whatever is behind
   *  it, or to nothing — which is a pointer miss, as if it were not there. */
  interactive?: (item: T) => boolean;
  children?: ReactNode;
}

const _m = new THREE.Matrix4();
const _color = new THREE.Color();

/** Remove the hits on dead instances from `intersects[start..]`, in place. */
export function dropDeadHits<T>(intersects: THREE.Intersection[], start: number, items: T[], interactive: (item: T) => boolean) {
  let w = start;
  for (let r = start; r < intersects.length; r++) {
    const hit = intersects[r];
    const item = hit.instanceId == null ? undefined : items[hit.instanceId];
    if (item === undefined || interactive(item)) intersects[w++] = hit;
  }
  intersects.length = w;
}

/** Wrap `mesh.raycast` so dead instances are never reported. Both getters are
 *  read per raycast, so the caller can swap items or predicate without touching
 *  the mesh. Returns the undo. */
export function deadenRaycast<T>(
  mesh: THREE.InstancedMesh,
  getItems: () => T[],
  getInteractive: () => ((item: T) => boolean) | undefined,
): () => void {
  const base = mesh.raycast;
  mesh.raycast = (raycaster, intersects) => {
    const start = intersects.length;
    base.call(mesh, raycaster, intersects);
    const interactive = getInteractive();
    if (interactive) dropDeadHits(intersects, start, getItems(), interactive);
  };
  return () => { mesh.raycast = base; };
}

export function StaticInstances<T>({
  items, compose, colorOf, geometry, material, castShadow = false, cullable = false,
  onClick, onPointerOver, onPointerMove, onPointerOut, interactive, children,
}: Props<T>) {
  const ref = useRef<THREE.InstancedMesh>(null);
  const itemsRef = useRef(items);
  itemsRef.current = items;
  const interactiveRef = useRef(interactive);
  interactiveRef.current = interactive;

  useLayoutEffect(() => {
    const mesh = ref.current;
    if (!mesh || typeof mesh.raycast !== "function") return; // jsdom renders the tag only
    return deadenRaycast(mesh, () => itemsRef.current, () => interactiveRef.current);
  }, []);

  useLayoutEffect(() => {
    const mesh = ref.current;
    if (!mesh || typeof mesh.setMatrixAt !== "function") return; // jsdom renders the tag only
    items.forEach((item, i) => {
      compose(item, _m);
      mesh.setMatrixAt(i, _m);
      if (colorOf) mesh.setColorAt(i, _color.set(colorOf(item)));
    });
    mesh.instanceMatrix.needsUpdate = true;
    if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
    // geometry is a dep: an LOD swap changes the per-instance extents the
    // culling bounds derive from.
    if (cullable) mesh.computeBoundingSphere();
  }, [items, compose, colorOf, cullable, geometry]);

  return (
    <instancedMesh
      ref={ref}
      args={[undefined, undefined, Math.max(1, items.length)]}
      frustumCulled={cullable}
      castShadow={castShadow}
      onClick={onClick}
      onPointerOver={onPointerOver}
      onPointerMove={onPointerMove}
      onPointerOut={onPointerOut}
    >
      {geometry && <primitive object={geometry} attach="geometry" />}
      {material && <primitive object={material} attach="material" />}
      {children}
    </instancedMesh>
  );
}
